import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Globe, Plus, Search, Trash2, Users } from "lucide-react";
import { 
  getShiftTemplatesService,
  addShiftTemplateService,
  deleteShiftTemplateService,
  getShiftAssignmentsService,
  assignShiftService, 
  getAllStaffService,
  getDefaultShiftService,
  updateDefaultShiftService
} from "../../services/api-service"; 

const ShiftManagement = () => {
  const [loading, setLoading] = useState(false);
  const [defaultLoading, setDefaultLoading] = useState(false);
  
  // Data States
  const [defaultShift, setDefaultShift] = useState({ start_time: "09:00", end_time: "18:00", late_grace_mins: 15, early_checkout_mins: 15, overtime_grace_mins: 30 });
  const [templates, setTemplates] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [assignments, setAssignments] = useState([]);

  // Filters for the main roster
  const [searchFilter, setSearchFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("ALL");

  // Modal State for Bulk Assignment
  const [managingShift, setManagingShift] = useState(null);
  const [modalSearch, setModalSearch] = useState("");

  // New Template Form State
  const [newTemplate, setNewTemplate] = useState({ name: "", start_time: "08:00", end_time: "16:00", late_grace_mins: 15, early_checkout_mins: 15, overtime_grace_mins: 30 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [defRes, tempRes, staffRes, assignRes] = await Promise.all([
        getDefaultShiftService(),
        getShiftTemplatesService(),
        getAllStaffService(),
        getShiftAssignmentsService()
      ]);

      if (defRes.data) setDefaultShift(defRes.data);
      setTemplates(tempRes.data?.data || []);
      setStaffList(staffRes.data || staffRes || []);
      setAssignments(assignRes.data?.data || []);
    } catch (error) {
      toast.error("Failed to load shift configurations.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDefault = async (e) => {
    e.preventDefault();
    setDefaultLoading(true);
    try {
      await updateDefaultShiftService(defaultShift);
      toast.success("Global default shift updated successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update default shift.");
    } finally {
      setDefaultLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addShiftTemplateService(newTemplate);
      toast.success(`Shift "${newTemplate.name}" created successfully!`);
      setNewTemplate({ name: "", start_time: "08:00", end_time: "16:00", late_grace_mins: 15, early_checkout_mins: 15, overtime_grace_mins: 30 });
      fetchData();
    } catch (error) {
      toast.error("Failed to add shift template.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Delete this shift? Staff assigned to it will revert to the Default Shift.")) return;
    try {
      await deleteShiftTemplateService(id);
      toast.success("Shift template deleted.");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete shift template.");
    }
  };

  const handleAssignShift = async (staffId, shiftId) => {
    try {
      await assignShiftService({ staff_id: staffId, shift_id: parseInt(shiftId) }); 
      fetchData(); 
      toast.success("Shift updated successfully");
    } catch (error) {
      toast.error("Failed to assign shift.");
    }
  };

  const getAssignedShiftId = (staffId) => {
    const assignment = assignments.find(a => String(a.staff_id) === String(staffId));
    return assignment ? assignment.shift_id : 0;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hourStr, minute] = timeString.split(":");
    const hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
  };

  const filteredStaffList = staffList.filter(staff => {
    const term = searchFilter.toLowerCase();
    const matchesText = staff.first_name?.toLowerCase().includes(term) || 
                        staff.last_name?.toLowerCase().includes(term) ||
                        staff.staff_id?.toLowerCase().includes(term);

    const assignment = assignments.find(a => String(a.staff_id) === String(staff.staff_id));
    const currentShiftId = assignment ? assignment.shift_id : 0;
    
    let matchesShift = true;
    if (shiftFilter === "DEFAULT") matchesShift = currentShiftId === 0;
    else if (shiftFilter !== "ALL") matchesShift = currentShiftId === parseInt(shiftFilter);

    return matchesText && matchesShift;
  });

  return (
    <div className="content">
      <div className="container-fluid py-4 position-relative">
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-bold mb-0 text-dark">Shift Management</h3>
            <p className="text-muted mb-0">Manage global defaults, create templates, and assign staff</p>
          </div>
        </div>

        <div className="row g-4">
          {/* Controls Column */}
          <div className="col-lg-4">
            
            {/* Global Default Settings */}
            <div className="card border-0 shadow-sm rounded-4 mb-4 bg-primary bg-opacity-10" style={{ border: "1px solid rgba(13, 110, 253, 0.2)" }}>
              <div className="card-body p-4">
                <h6 className="fw-bold text-primary mb-3 d-flex align-items-center">
                  <Globe size={18} className="me-2" /> Global Default Shift
                </h6>
                <form onSubmit={handleSaveDefault}>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                       <span className="d-block text-primary small fw-bold mb-1 text-uppercase" style={{letterSpacing: "0.5px"}}>Start Time</span>
                       <input type="time" className="form-control rounded-3 border-0 shadow-sm text-center fw-bold text-primary py-2" value={defaultShift.start_time} onChange={(e) => setDefaultShift({...defaultShift, start_time: e.target.value})} required />
                    </div>
                    <div className="col-6">
                       <span className="d-block text-primary small fw-bold mb-1 text-uppercase" style={{letterSpacing: "0.5px"}}>End Time</span>
                       <input type="time" className="form-control rounded-3 border-0 shadow-sm text-center fw-bold text-primary py-2" value={defaultShift.end_time} onChange={(e) => setDefaultShift({...defaultShift, end_time: e.target.value})} required />
                    </div>
                  </div>
                  
                  <div className="row g-2 mb-4">
                    <div className="col-4">
                      <span className="d-block text-primary small fw-bold mb-1 text-uppercase text-center" style={{fontSize: '0.65rem', letterSpacing: "0.5px"}}>Late</span>
                      <div className="input-group input-group-sm shadow-sm rounded-3 overflow-hidden">
                        <input type="number" className="form-control border-0 text-center fw-bold text-secondary" value={defaultShift.late_grace_mins} onChange={(e) => setDefaultShift({...defaultShift, late_grace_mins: parseInt(e.target.value) || 0})} required />
                        <span className="input-group-text bg-white border-0 text-muted px-2" style={{fontSize: '0.7rem'}}>m</span>
                      </div>
                    </div>
                    <div className="col-4">
                      <span className="d-block text-primary small fw-bold mb-1 text-uppercase text-center" style={{fontSize: '0.65rem', letterSpacing: "0.5px"}}>Early Exit</span>
                      <div className="input-group input-group-sm shadow-sm rounded-3 overflow-hidden">
                        <input type="number" className="form-control border-0 text-center fw-bold text-secondary" value={defaultShift.early_checkout_mins} onChange={(e) => setDefaultShift({...defaultShift, early_checkout_mins: parseInt(e.target.value) || 0})} required />
                        <span className="input-group-text bg-white border-0 text-muted px-2" style={{fontSize: '0.7rem'}}>m</span>
                      </div>
                    </div>
                    <div className="col-4">
                      <span className="d-block text-primary small fw-bold mb-1 text-uppercase text-center" style={{fontSize: '0.65rem', letterSpacing: "0.5px"}}>Overtime</span>
                      <div className="input-group input-group-sm shadow-sm rounded-3 overflow-hidden">
                        <input type="number" className="form-control border-0 text-center fw-bold text-secondary" value={defaultShift.overtime_grace_mins} onChange={(e) => setDefaultShift({...defaultShift, overtime_grace_mins: parseInt(e.target.value) || 0})} required />
                        <span className="input-group-text bg-white border-0 text-muted px-2" style={{fontSize: '0.7rem'}}>m</span>
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary w-100 rounded-pill fw-bold shadow-sm" disabled={defaultLoading}>
                    {defaultLoading ? "Updating..." : "Update Default Shift"}
                  </button>
                </form>
              </div>
            </div>

            {/* Shift Templates Card */}
            <div className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-header bg-transparent border-0 pt-4 pb-2 px-4">
                <h5 className="card-title fw-bold text-dark mb-0 d-flex align-items-center">
                  <Plus size={20} className="text-primary me-2" /> Create New Shift
                </h5>
              </div>
              <div className="card-body px-4 pb-4">
                <form onSubmit={handleCreateTemplate}>
                  <div className="mb-3">
                    <span className="d-block text-secondary small fw-bold mb-1 text-uppercase" style={{letterSpacing: "0.5px"}}>Shift Name</span>
                    <input type="text" className="form-control bg-light rounded-3 border-0 py-2" placeholder="e.g. Night Shift" value={newTemplate.name} onChange={e => setNewTemplate({...newTemplate, name: e.target.value})} required />
                  </div>
                  
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <span className="d-block text-secondary small fw-bold mb-1 text-uppercase" style={{letterSpacing: "0.5px"}}>Start Time</span>
                      <input type="time" className="form-control bg-light rounded-3 border-0 text-center py-2" value={newTemplate.start_time} onChange={e => setNewTemplate({...newTemplate, start_time: e.target.value})} required />
                    </div>
                    <div className="col-6">
                      <span className="d-block text-secondary small fw-bold mb-1 text-uppercase" style={{letterSpacing: "0.5px"}}>End Time</span>
                      <input type="time" className="form-control bg-light rounded-3 border-0 text-center py-2" value={newTemplate.end_time} onChange={e => setNewTemplate({...newTemplate, end_time: e.target.value})} required />
                    </div>
                  </div>
                  
                  <div className="row g-2 mb-4">
                    <div className="col-4">
                      <span className="d-block text-secondary small fw-bold mb-1 text-uppercase text-center" style={{fontSize: '0.65rem', letterSpacing: "0.5px"}}>Late Limit</span>
                      <div className="input-group input-group-sm rounded-3 overflow-hidden">
                        <input type="number" className="form-control bg-light border-0 text-center" value={newTemplate.late_grace_mins} onChange={(e) => setNewTemplate({...newTemplate, late_grace_mins: parseInt(e.target.value) || 0})} required />
                        <span className="input-group-text bg-light border-0 text-muted px-2" style={{fontSize: '0.7rem'}}>m</span>
                      </div>
                    </div>
                    <div className="col-4">
                      <span className="d-block text-secondary small fw-bold mb-1 text-uppercase text-center" style={{fontSize: '0.65rem', letterSpacing: "0.5px"}}>Early Exit</span>
                      <div className="input-group input-group-sm rounded-3 overflow-hidden">
                        <input type="number" className="form-control bg-light border-0 text-center" value={newTemplate.early_checkout_mins} onChange={(e) => setNewTemplate({...newTemplate, early_checkout_mins: parseInt(e.target.value) || 0})} required />
                        <span className="input-group-text bg-light border-0 text-muted px-2" style={{fontSize: '0.7rem'}}>m</span>
                      </div>
                    </div>
                    <div className="col-4">
                      <span className="d-block text-secondary small fw-bold mb-1 text-uppercase text-center" style={{fontSize: '0.65rem', letterSpacing: "0.5px"}}>Overtime</span>
                      <div className="input-group input-group-sm rounded-3 overflow-hidden">
                        <input type="number" className="form-control bg-light border-0 text-center" value={newTemplate.overtime_grace_mins} onChange={(e) => setNewTemplate({...newTemplate, overtime_grace_mins: parseInt(e.target.value) || 0})} required />
                        <span className="input-group-text bg-light border-0 text-muted px-2" style={{fontSize: '0.7rem'}}>m</span>
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-dark w-100 rounded-pill fw-bold shadow-sm" disabled={loading}>
                    {loading ? "Creating..." : "Save Custom Shift"}
                  </button>
                </form>
              </div>
            </div>

            {/* Available Shift Templates List */}
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-header bg-transparent border-0 pt-4 pb-2 px-4">
                <h6 className="card-title fw-bold text-dark mb-0">Available Shift Templates</h6>
              </div>
              <div className="card-body px-4 pb-4">
                <div className="list-group list-group-flush">
                  <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 mb-2 bg-light rounded px-3 py-2">
                    <div>
                      <h6 className="mb-0 fw-bold text-dark">Default Shift</h6>
                      <small className="text-muted d-block">{formatTime(defaultShift.start_time)} - {formatTime(defaultShift.end_time)}</small>
                    </div>
                    <span className="badge bg-secondary">System</span>
                  </div>
                  
                  {templates.map(temp => (
                    <div key={temp.id} className="list-group-item border-0 px-0 mb-2 border rounded px-3 py-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-0 fw-bold text-primary">{temp.name}</h6>
                          <small className="text-muted d-block">{formatTime(temp.start_time)} - {formatTime(temp.end_time)}</small>
                          <small className="text-muted" style={{fontSize: '0.7rem'}}>Late: {temp.late_grace_mins}m | Early: {temp.early_checkout_mins}m | OT: {temp.overtime_grace_mins}m</small>
                        </div>
                        <button className="btn btn-sm text-danger border-0 bg-transparent" onClick={() => handleDeleteTemplate(temp.id)}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                      <button 
                        className="btn btn-sm btn-outline-primary w-100 rounded-pill fw-bold mt-1 d-flex justify-content-center align-items-center"
                        onClick={() => setManagingShift(temp)}
                      >
                        <Users size={16} className="me-2" /> Manage Staff
                      </button>
                    </div>
                  ))}
                  {templates.length === 0 && (
                    <div className="text-muted small text-center py-2">No custom shifts created yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Roster Assignment Column */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-header bg-transparent border-0 pt-4 pb-2 px-4 d-flex justify-content-between align-items-center">
                <h5 className="card-title fw-bold text-dark mb-0 d-flex align-items-center">
                  <Users size={20} className="text-primary me-2" /> Employee Roster
                </h5>
                <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">Total: {filteredStaffList.length}</span>
              </div>
              <div className="card-body px-4 pb-4">
                
                {/* Search & Filters */}
                <div className="d-flex gap-2 mb-3">
                  <div className="position-relative flex-grow-1">
                    <Search size={16} className="text-muted position-absolute" style={{ left: '12px', top: '12px' }} />
                    <input type="text" className="form-control bg-light rounded-3 border-0 py-2 ps-5" placeholder="Search employee name or ID..." value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} />
                  </div>
                  <select className="form-select bg-light rounded-3 border-0 w-auto fw-semibold text-secondary" value={shiftFilter} onChange={(e) => setShiftFilter(e.target.value)}>
                    <option value="ALL">All Shifts</option>
                    <option value="DEFAULT">Default Shift Only</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div className="table-responsive" style={{ maxHeight: "750px", overflowY: "auto" }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light text-secondary sticky-top">
                      <tr>
                        <th className="border-0 rounded-start">Employee</th>
                        <th className="border-0">Department</th>
                        <th className="border-0 rounded-end">Assigned Shift</th>
                      </tr>
                    </thead>
                    <tbody className="border-top-0">
                      {filteredStaffList.length > 0 ? (
                        filteredStaffList.map((staff) => {
                          const assignment = assignments.find(a => String(a.staff_id) === String(staff.staff_id));
                          const currentShiftId = assignment ? assignment.shift_id : 0;

                          return (
                            <tr key={staff.staff_id} className="border-bottom">
                              <td className="py-2">
                                <h6 className="mb-0 fw-bold">{staff.first_name} {staff.last_name}</h6>
                                <small className="text-muted">{staff.staff_id}</small>
                              </td>
                              <td><span className="badge bg-light text-dark border px-2 py-1 rounded-pill">{staff.department || "—"}</span></td>
                              <td>
                                <select 
                                  className={`form-select form-select-sm rounded-pill border-0 fw-bold px-3 py-1 ${currentShiftId === 0 ? 'bg-light text-secondary' : 'bg-primary bg-opacity-10 text-primary'}`}
                                  value={currentShiftId}
                                  onChange={(e) => handleAssignShift(staff.staff_id, e.target.value)}
                                >
                                  <option value={0}>Default ({formatTime(defaultShift.start_time)} - {formatTime(defaultShift.end_time)})</option>
                                  {templates.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} ({formatTime(t.start_time)} - {formatTime(t.end_time)})</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan="3" className="text-center py-4 text-muted">No employees found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          
        </div>

        {/* =========================================
            BULK MANAGE MODAL
        ========================================= */}
        {managingShift && (
          <div className="modal-backdrop fade show" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}></div>
        )}
        <div className={`modal fade ${managingShift ? 'show d-block' : ''}`} tabIndex="-1" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0 pt-4 px-4">
                <div>
                  <h5 className="fw-bold text-dark mb-0">
                    Assign Staff to <span className="text-primary">{managingShift?.name}</span>
                  </h5>
                  <p className="text-muted small mb-0 mt-1">Select employees to assign them to this shift.</p>
                </div>
                <button type="button" className="btn-close align-self-start" onClick={() => { setManagingShift(null); setModalSearch(""); }}></button>
              </div>
              <div className="modal-body p-4">
                <div className="position-relative mb-3">
                  <Search size={16} className="text-muted position-absolute" style={{ left: '12px', top: '10px' }} />
                  <input 
                    type="text" 
                    className="form-control bg-light rounded-3 border-0 py-2 ps-5" 
                    placeholder="Search staff..." 
                    value={modalSearch} 
                    onChange={e => setModalSearch(e.target.value)} 
                  />
                </div>
                
                <div className="list-group list-group-flush border rounded overflow-hidden">
                  {staffList.filter(s => {
                    const term = modalSearch.toLowerCase();
                    return s.first_name?.toLowerCase().includes(term) || s.last_name?.toLowerCase().includes(term);
                  }).map(staff => {
                    const assignment = assignments.find(a => String(a.staff_id) === String(staff.staff_id));
                    const isInShift = assignment && assignment.shift_id === managingShift?.id;
                    
                    return (
                      <label key={staff.staff_id} className={`list-group-item d-flex justify-content-between align-items-center border-bottom-0 border-top cursor-pointer ${isInShift ? 'bg-primary bg-opacity-10' : ''}`} style={{cursor: 'pointer'}}>
                        <div>
                          <h6 className={`mb-0 fw-bold ${isInShift ? 'text-primary' : 'text-dark'}`}>{staff.first_name} {staff.last_name}</h6>
                          <small className="text-muted">{staff.staff_id} • {staff.department || "—"}</small>
                        </div>
                        <input 
                          type="checkbox" 
                          className="form-check-input mt-0" 
                          style={{transform: "scale(1.3)"}} 
                          checked={isInShift || false} 
                          onChange={(e) => {
                            const shiftId = e.target.checked ? managingShift.id : 0;
                            handleAssignShift(staff.staff_id, shiftId);
                          }} 
                        />
                      </label>
                    )
                  })}
                </div>
              </div>
              <div className="modal-footer border-0 pb-4 px-4 pt-0">
                <button className="btn btn-primary rounded-pill w-100 fw-bold" onClick={() => { setManagingShift(null); setModalSearch(""); }}>Done</button>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default ShiftManagement;
